import { useEffect, useState } from "react";
import { useMonyawn } from "../../context/MonyawnContext";
import { clearTelemetryEvents } from "../../lib/telemetry";
import {
  getPrivacyPreferences,
  PrivacyPreferences,
  savePrivacyPreferences,
} from "../../lib/privacyPreferences";

export function PrivacyControlsPanel() {
  const { patchState, setNotice } = useMonyawn();
  const [preferences, setPreferences] = useState<PrivacyPreferences>(getPrivacyPreferences());

  useEffect(() => {
    setPreferences(getPrivacyPreferences());
  }, []);

  const handleDiagnosticsToggle = (enabled: boolean) => {
    const next = { diagnosticsEnabled: enabled };
    setPreferences(next);
    savePrivacyPreferences(next);
    if (!enabled) {
      clearTelemetryEvents();
    }
    setNotice({
      tone: "success",
      message: enabled
        ? "Optional diagnostics enabled. Reload the app if you want runtime diagnostics applied immediately."
        : "Optional diagnostics disabled and local diagnostics history cleared.",
    });
  };

  return (
    <section className="rounded-[1.75rem] border border-black/10 bg-white/80 p-6">
      <h2 className="text-xl font-bold text-brand-ink tracking-[-0.02em]">Privacy controls</h2>
      <div className="mt-4 space-y-4">
        <label className="flex items-start gap-3 rounded-[1.25rem] border border-black/10 bg-brand-surface p-4">
          <input
            type="checkbox"
            checked={preferences.diagnosticsEnabled}
            onChange={(event) => handleDiagnosticsToggle(event.target.checked)}
            className="mt-1 h-4 w-4 rounded border-black/20 text-brand-accent focus:ring-brand-accent"
          />
          <span className="text-sm text-brand-muted leading-relaxed">
            Allow optional diagnostics. This enables local telemetry storage and, if configured,
            Sentry error monitoring. Diagnostics are off by default.
          </span>
        </label>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            className="rounded-full border border-black/10 bg-white px-4 py-2 text-sm font-semibold text-brand-ink hover:bg-black/5 transition-colors"
            onClick={() => {
              clearTelemetryEvents();
              setNotice({ tone: "success", message: "Local diagnostics history cleared." });
            }}
          >
            Clear diagnostics history
          </button>
          <button
            type="button"
            className="rounded-full border border-black/10 bg-white px-4 py-2 text-sm font-semibold text-brand-ink hover:bg-black/5 transition-colors"
            onClick={() => {
              patchState(
                (current) => ({
                  ...current,
                  opportunities: [],
                  proof: [],
                  candidateProfiles: [],
                  bags: [],
                  events: [],
                  checkpoints: [],
                  tasks: [],
                  escalations: [],
                  memos: [],
                  dms: [],
                  candidateStories: [],
                  sensitiveSupportProfiles: [],
                  releaseProofReviews: [],
                  outcomes: [],
                }),
                "Local workspace reset from privacy controls.",
              );
              setNotice({
                tone: "success",
                message:
                  "Local workspace data reset. Browser-level storage may still retain non-workspace preferences until you clear browser data.",
              });
            }}
          >
            Reset local workspace data
          </button>
        </div>
      </div>
    </section>
  );
}
